import { CommonModule, DatePipe, DecimalPipe, UpperCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  AgentProfileDto,
  AgentProfileId,
  AgentProfilePublicDto,
  AgentServiceOffering,
  AgentSpecialty,
  AgentUserPublicDto,
  FRENCH_DEPARTMENTS_MAP,
  FRENCH_REGIONS,
  FrenchDepartment,
  OpeningHoursDto,
  PropertyId,
  PropertyOwnerSummaryDto,
} from '@workspace/data-types';
import { AuraOpeningHoursDisplayComponent, DialogLayoutAuraComponent } from '@workspace/ng-aura';
import { ApiService } from '@workspace/ng-http';
import { catchError, finalize, of, tap } from 'rxjs';
import { AgentCardDisplayComponent } from '../../../agent-card';

export interface DiscussionProfileDialogData {
  participantId: string;
  participantType?: 'agent' | 'seller' | 'owner' | 'user';
  participantName?: string;
  participantAvatarUrl?: string | null;
  propertyId?: string;
  agentProfile?: AgentProfilePublicDto | AgentProfileDto;
  ownerSummary?: PropertyOwnerSummaryDto;
}

export type AgentProfileTab = 'services' | 'sector' | 'card';

@Component({
  selector: 'etxe-discussion-profile-dialog',
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    DialogLayoutAuraComponent,
    AgentCardDisplayComponent,
    AuraOpeningHoursDisplayComponent,
    DatePipe,
    DecimalPipe,
    UpperCasePipe,
  ],
  templateUrl: './discussion-profile-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiscussionProfileDialogComponent {
  readonly dialogRef = inject(MatDialogRef<DiscussionProfileDialogComponent>);
  readonly data = inject<DiscussionProfileDialogData>(MAT_DIALOG_DATA);
  private readonly api = inject(ApiService);
  private readonly destroyRef = inject(DestroyRef);

  readonly agentProfile = signal<AgentProfilePublicDto | AgentProfileDto | null>(null);
  readonly ownerSummary = signal<PropertyOwnerSummaryDto | null>(null);
  readonly isLoading = signal<boolean>(true);
  readonly hasError = signal<boolean>(false);
  readonly activeTab = signal<AgentProfileTab>('services');

  readonly isOwner = computed(() => {
    return this.data.participantType === 'owner' || this.data.participantType === 'seller';
  });

  readonly isAgent = computed(() => {
    return this.data.participantType === 'agent';
  });

  readonly hasProfile = computed(() => {
    return this.isAgent() ? Boolean(this.agentProfile()) : Boolean(this.ownerSummary());
  });

  // Common user computed properties
  readonly agentUser = computed<AgentUserPublicDto | null>(() => {
    const u = this.agentProfile()?.user;
    return typeof u === 'object' && u !== null ? (u as AgentUserPublicDto) : null;
  });

  readonly firstName = computed(() => {
    if (this.isOwner()) {
      return this.ownerSummary()?.firstName || this.data.participantName || 'Propriétaire vendeur';
    }
    return this.agentUser()?.identity?.firstName || this.data.participantName || '';
  });

  readonly lastName = computed(() => {
    if (this.isOwner()) {
      return '';
    }
    return this.agentUser()?.identity?.lastName || '';
  });

  readonly fullName = computed(() => {
    if (this.isOwner()) {
      return this.firstName() || 'Propriétaire vendeur';
    }
    const f = this.firstName();
    const l = this.lastName();
    if (!f && !l) {
      return this.data.participantName || 'Agent immobilier certifié';
    }
    return `${f} ${l}`.trim();
  });

  readonly effectiveAvatarUrl = computed(() => {
    if (this.isOwner()) {
      return null;
    }
    return this.agentUser()?.identity?.avatar?.url || this.data.participantAvatarUrl || null;
  });

  readonly initials = computed(() => {
    const f = this.firstName().charAt(0);
    const l = this.lastName().charAt(0);
    if (!f && !l) return 'UT';
    return (f + (l || '')).toUpperCase();
  });

  readonly memberSince = computed(() => {
    if (this.isOwner()) {
      return this.ownerSummary()?.memberSince || null;
    }
    return this.agentUser()?.createdAt || this.agentProfile()?.createdAt || null;
  });

  readonly isProSectionPrivate = computed(() => {
    const p = this.agentProfile();
    return p && 'settings' in p ? (p as AgentProfileDto).settings?.visibility?.isPublic === false : false;
  });

  readonly agentType = computed(() => {
    return this.agentProfile()?.type === 'titulaire' ? 'Titulaire de carte T' : 'Mandataire immobilier';
  });

  readonly cards = computed(() => this.agentProfile()?.cards);
  readonly activeCard = computed(() => this.cards()?.activeCard ?? null);
  readonly isCardActive = computed(() => this.activeCard()?.isActive !== false);

  readonly activity = computed(() => this.agentProfile()?.activity);
  readonly network = computed(() => this.activity()?.network || null);
  readonly specialties = computed(() => this.activity()?.specialties || []);
  readonly bio = computed(() => this.activity()?.bio || '');
  readonly services = computed(() => this.activity()?.services || []);
  readonly averageCommissionRate = computed(() => this.activity()?.averageCommissionRate ?? null);

  readonly responsiveness = computed(() => {
    if (this.isOwner()) {
      return this.ownerSummary()?.responsiveness ?? null;
    }
    return this.agentUser()?.responsiveness ?? null;
  });
  readonly responsiveBadgeLabel = computed(() =>
    this.isOwner() ? 'Propriétaire très réactif' : 'Agent très réactif',
  );
  readonly isHighlyResponsive = computed(() => this.responsiveness()?.isHighlyResponsive ?? false);
  readonly responseRatePercent = computed(() => this.responsiveness()?.responseRatePercent ?? null);
  readonly averageResponseTimeHours = computed(() => this.responsiveness()?.averageResponseTimeHours ?? null);
  readonly formattedResponseTime = computed(() => {
    const hours = this.averageResponseTimeHours();
    if (hours == null) return null;
    if (hours < 1) return '< 1 h';
    if (hours === 1) return '1 h';
    const rounded = Math.round(hours * 10) / 10;
    return `${rounded % 1 === 0 ? rounded : rounded.toFixed(1).replace('.', ',')} h`;
  });

  readonly intervention = computed(() => this.agentProfile()?.intervention);
  readonly primaryRegion = computed(() => this.intervention()?.primaryRegion || null);
  readonly interventionZones = computed(() => this.intervention()?.zones || []);
  readonly preferredCities = computed(() => this.intervention()?.preferredCities || []);

  readonly work = computed(() => this.agentProfile()?.work);
  readonly openingHours = computed<OpeningHoursDto | null>(() => {
    return (this.work()?.openingHours ?? null) as OpeningHoursDto | null;
  });

  readonly isVacationMode = computed(() => {
    const p = this.agentProfile();
    if (!p) return false;
    if ('isVacationMode' in p && p.isVacationMode !== undefined) {
      return Boolean(p.isVacationMode);
    }
    return Boolean((p as AgentProfileDto).settings?.visibility?.isVacationMode);
  });

  readonly hasOpeningHours = computed<boolean>(() => {
    const hours = this.openingHours();
    if (!hours) return false;
    const days: Array<'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday'> = [
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
      'sunday',
    ];
    return days.some((day) => Boolean(hours[day]?.isOpen));
  });

  readonly openingHoursStatus = computed(() => {
    const hours = this.openingHours();
    if (!hours) return null;

    const currentJsDay = new Date().getDay();
    const dayKeys: Array<{
      key: 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';
      jsDay: number;
    }> = [
      { key: 'monday', jsDay: 1 },
      { key: 'tuesday', jsDay: 2 },
      { key: 'wednesday', jsDay: 3 },
      { key: 'thursday', jsDay: 4 },
      { key: 'friday', jsDay: 5 },
      { key: 'saturday', jsDay: 6 },
      { key: 'sunday', jsDay: 0 },
    ];

    const todayConfig = dayKeys.find((d) => d.jsDay === currentJsDay);
    const today = todayConfig ? hours[todayConfig.key] : undefined;

    if (!today || !today.isOpen) {
      return {
        isOpen: false,
        text: 'Fermé aujourd’hui',
        badgeClass: 'bg-neutral-2 text-neutral-10 border-neutral-3',
      };
    }

    if (today.byAppointmentOnly) {
      return {
        isOpen: true,
        text: 'Sur rendez-vous aujourd’hui',
        badgeClass: 'bg-primary-2 text-primary-11 border-primary-4',
      };
    }

    const slots: string[] = [];
    if (today.morningOpen && today.morningClose) {
      slots.push(`${today.morningOpen} - ${today.morningClose}`);
    }
    if (today.afternoonOpen && today.afternoonClose) {
      slots.push(`${today.afternoonOpen} - ${today.afternoonClose}`);
    }

    if (slots.length === 0) {
      return {
        isOpen: false,
        text: 'Fermé aujourd’hui',
        badgeClass: 'bg-neutral-2 text-neutral-10 border-neutral-3',
      };
    }

    return {
      isOpen: true,
      text: `Ouvert aujourd’hui (${slots.join(' & ')})`,
      badgeClass: 'bg-primary-2 text-primary-11 border-primary-4',
    };
  });

  readonly specialtyMeta: Record<string, { label: string; icon: string }> = {
    [AgentSpecialty.APARTMENT]: { label: 'Appartements', icon: 'apartment' },
    [AgentSpecialty.HOUSE]: { label: 'Maisons & villas', icon: 'home' },
    [AgentSpecialty.PRESTIGE]: { label: 'Prestige & luxe', icon: 'diamond' },
    [AgentSpecialty.BUILDING]: { label: 'Immeubles de rapport', icon: 'domain' },
    [AgentSpecialty.LAND]: { label: 'Terrains constructibles', icon: 'landscape' },
    [AgentSpecialty.COMMERCIAL]: { label: 'Commerces & locaux', icon: 'storefront' },
  };

  readonly serviceMeta: Record<string, { label: string; icon: string }> = {
    [AgentServiceOffering.PHOTO_PRO]: { label: 'Photographie professionnelle', icon: 'photo_camera' },
    [AgentServiceOffering.VIRTUAL_TOUR]: { label: 'Visite immersive 3D', icon: 'view_in_ar' },
    [AgentServiceOffering.DRONE]: { label: 'Prises de vue par drone', icon: 'flight' },
    [AgentServiceOffering.HOME_STAGING]: { label: 'Home staging & valorisation', icon: 'auto_awesome' },
    [AgentServiceOffering.FINANCIAL_SCREENING]: {
      label: 'Filtrage financier acquéreurs',
      icon: 'verified_user',
    },
    [AgentServiceOffering.REPORT_24H]: { label: 'Compte-rendu de visite sous 24h', icon: 'schedule' },
    [AgentServiceOffering.PREMIUM_PORTALS]: { label: 'Diffusion portails premium', icon: 'campaign' },
    [AgentServiceOffering.NOTARY_SUPPORT]: { label: 'Accompagnement notarial', icon: 'gavel' },
  };

  readonly dialogTitle = computed(() => {
    if (this.isAgent()) {
      return "Profil de l'agent";
    }
    if (this.isOwner()) {
      return 'Profil du propriétaire';
    }
    return 'Profil utilisateur';
  });

  constructor() {
    this.fetchProfile();
  }

  private fetchProfile(): void {
    this.isLoading.set(true);
    this.hasError.set(false);

    // 1. OWNER CASE: load succinct owner summary via dedicated property owner route
    if (this.isOwner()) {
      if (this.data.ownerSummary) {
        this.ownerSummary.set(this.data.ownerSummary);
        this.hasError.set(false);
        this.isLoading.set(false);
        this.dialogRef.updateSize('480px');
        return;
      }

      if (this.data.propertyId) {
        this.api
          .business()
          .etxe()
          .properties()
          .agent()
          .owner(this.data.propertyId as unknown as PropertyId)
          .pipe(
            tap((summary) => {
              this.ownerSummary.set(summary);
              this.hasError.set(false);
            }),
            catchError(() => {
              this.ownerSummary.set({
                id: this.data.participantId,
                firstName: this.data.participantName || 'Propriétaire vendeur',
                memberSince: undefined,
                isVerified: true,
                rating: 5.0,
                responseTime: '< 1 h',
              });
              return of(null);
            }),
            finalize(() => {
              this.isLoading.set(false);
              this.dialogRef.updateSize('480px');
            }),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe();
      } else {
        this.ownerSummary.set({
          id: this.data.participantId,
          firstName: this.data.participantName || 'Propriétaire vendeur',
          memberSince: undefined,
          isVerified: true,
          rating: 5.0,
          responseTime: '< 1 h',
        });
        this.isLoading.set(false);
        this.dialogRef.updateSize('480px');
      }
      return;
    }

    // 2. AGENT CASE: use preloaded profile when provided, otherwise load via public agent route
    if (this.data.agentProfile) {
      this.agentProfile.set(this.data.agentProfile);
      this.hasError.set(false);
      this.isLoading.set(false);
      this.dialogRef.updateSize('760px');
      return;
    }

    const targetId = this.data.participantId;
    if (!targetId) {
      this.isLoading.set(false);
      this.hasError.set(true);
      return;
    }

    this.api
      .business()
      .etxe()
      .public()
      .agents()
      .get(targetId as unknown as AgentProfileId)
      .pipe(
        tap((agent) => {
          this.agentProfile.set(agent);
          this.hasError.set(false);
          this.dialogRef.updateSize('760px');
        }),
        catchError((err) => {
          console.error('Failed to load agent profile for dialog:', err);
          this.hasError.set(true);
          return of(null);
        }),
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  getSpecialtyInfo(key: string) {
    return this.specialtyMeta[key] || { label: key, icon: 'category' };
  }

  getServiceInfo(key: string) {
    return this.serviceMeta[key] || { label: key, icon: 'check_circle' };
  }

  getRegionName(regCode?: string | null): string {
    if (!regCode) return '';
    return FRENCH_REGIONS.find((r) => r.code === regCode)?.name || regCode;
  }

  getDepartmentLabel(deptCode: string): string {
    const info = FRENCH_DEPARTMENTS_MAP[deptCode as FrenchDepartment];
    return info ? `${info.name} (${info.code})` : deptCode;
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
